import { ColumnTable, Icon, Table, useMobile } from '@octano/global-ui';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'reactstrap';

import { getAllReports } from '../../../../api/requests/reports';
import { Report } from '../../../../types/Reports';
import { useReportsColumns } from './hooks/useReportsColumns';
import DownloadReportModal, {
  DownloadReportModalMethods,
} from './parts/DownloadReportModal';

const ITEMS_PER_PAGE = 60;

const filterColumnsByMobile = (
  onMobile: boolean,
  columns: ColumnTable<Report>[],
) => {
  return columns.filter((column) => {
    if (!onMobile) return true;
    return column.columnName !== 'description';
  });
};

const ReportSync = () => {
  const { t } = useTranslation();
  const downloaderRef = useRef<DownloadReportModalMethods>(null);
  const prefix = 'reports';

  const isMobile = useMobile();

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalReports, setTotalReports] = useState(0);

  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [reports, setReports] = useState<Report[]>([]);
  const [error, setError] = useState(false);

  const handlePressDownload = useCallback((row: Report) => {
    downloaderRef?.current?.open(row);
  }, []);

  const TABLE_COLUMNS = useReportsColumns({
    onPressDownload: handlePressDownload,
  });

  useEffect(() => {
    const fetchReportsList = async () => {
      setIsLoadingReports(true);
      const { data, error: fetchError } = await getAllReports();

      if (fetchError) return setError(true);

      setTotalPages(data?.total_pages || 0);
      setTotalReports(data?.total || 0);

      const orderedReports = data?.data.sort((rp1, rp2) =>
        rp1.name > rp2.name ? 1 : -1,
      );
      setReports(orderedReports || []);

      setIsLoadingReports(false);
    };

    fetchReportsList();
  }, []);

  return (
    <>
      <Card className="p-4" style={{ minHeight: 400 }}>
        <p className="mb-4 mt-2">{t(`${prefix}.pageDescription`)}</p>
        {error ? (
          <div className="d-flex justify-content-center align-items-center text-center">
            <div className="p-5" style={{ maxWidth: 500 }}>
              <Icon name="warning" size="50px" />
              <h1 className="fs-20 mt-4 text-dark">
                {t(`${prefix}.errors.reportsListError.title`)}
              </h1>
              <p className="fs-16 mt-3">
                {t(`${prefix}.errors.reportsListError.subtitle`)}
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <Table
              data={reports}
              columns={filterColumnsByMobile(isMobile, TABLE_COLUMNS)}
              pagination={{
                currentPage: currentPage + 1,
                itemsPerPage: ITEMS_PER_PAGE,
                totalItems: totalReports,
                totalPages: totalPages,
                onChangePage: (page) => setCurrentPage(page),
              }}
              isLoadingResults={isLoadingReports}
              loadingView={{
                title: t(`${prefix}.loading.generatingFile.title`),
                subtitle: t(`${prefix}.loading.generatingFile.description`),
              }}
              striped
            />
          </div>
        )}
      </Card>
      <DownloadReportModal ref={downloaderRef} />
    </>
  );
};

export default ReportSync;
